import type { ReactNode } from 'react';
import { CalendarBlankIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import {
  formatDayMonth,
  formatWeekdayDayMonth,
  parseDateOnly,
  toDateOnly,
} from '@voll-intelligence/dates';

/** Quantos dias a faixa mostra (frame 04 do "Conversacional → Bus"). */
const STRIP_SIZE = 5;

export type BusDayStripBounds = { readonly min?: string; readonly max?: string };

const shift = (day: Date, by: number): Date =>
  new Date(day.getFullYear(), day.getMonth(), day.getDate() + by);

/**
 * Os dias da faixa (`YYYY-MM-DD`): o buscado no meio, dois de cada lado. A
 * janela nunca começa antes de hoje nem do `min`, e nunca passa do `max` — ela
 * desliza em vez de mostrar um dia que não se pode buscar, e encolhe quando o
 * intervalo inteiro tem menos de cinco dias. Vazio se `day` é ilegível.
 */
export function busDayWindow(day: string, today: string, bounds: BusDayStripBounds = {}): string[] {
  const picked = parseDateOnly(day);
  const now = parseDateOnly(today);
  if (!picked || !now) return [];
  const min = parseDateOnly(bounds.min);
  const max = parseDateOnly(bounds.max);
  const floor = min && min > now ? min : now;
  let start = shift(picked, -Math.floor(STRIP_SIZE / 2));
  if (max && shift(start, STRIP_SIZE - 1) > max) start = shift(max, -(STRIP_SIZE - 1));
  if (start < floor) start = floor;
  const days: string[] = [];
  for (let i = 0; i < STRIP_SIZE; i += 1) {
    const candidate = shift(start, i);
    if (max && candidate > max) break;
    days.push(toDateOnly(candidate));
  }
  return days;
}

export type BusDayStripProps = {
  /** O dia buscado da perna em cena (`YYYY-MM-DD`). */
  day: string;
  /** Hoje, no fuso da pessoa: o piso da faixa. */
  today: string;
  bounds?: BusDayStripBounds;
  locale?: string;
  /** O nome acessível do grupo ("Buscar em outro dia"). */
  label: string;
  /** Uma busca em voo, ou o card congelado: os dias travam. */
  disabled?: boolean;
  onSelect(day: string): void;
};

/**
 * A faixa de dias da seleção curta de ônibus na conversa (frame 04): o dia
 * buscado em destaque e os vizinhos. Tocar um dia é pedir uma busca nova — a
 * faixa não filtra a lista, quem decide é o host.
 */
export function BusDayStrip({
  day,
  today,
  bounds,
  locale,
  label,
  disabled = false,
  onSelect,
}: Readonly<BusDayStripProps>): ReactNode {
  const days = busDayWindow(day, today, bounds);
  const now = parseDateOnly(today) ?? undefined;
  if (days.length < 2) return null;
  return (
    <div role="group" aria-label={label} className="grid auto-cols-fr grid-flow-col gap-2">
      {days.map((candidate) => {
        const date = parseDateOnly(candidate);
        if (!date) return null;
        const current = candidate === day;
        return (
          <Button
            key={candidate}
            type="button"
            size="sm"
            variant={current ? 'default' : 'outline'}
            className="min-w-0"
            aria-pressed={current}
            aria-label={formatWeekdayDayMonth(date, locale, now)}
            disabled={disabled}
            onClick={() => {
              if (!current) onSelect(candidate);
            }}
          >
            <CalendarBlankIcon aria-hidden="true" className="max-sm:hidden" />
            <span className="truncate">{formatDayMonth(date, locale, now)}</span>
          </Button>
        );
      })}
    </div>
  );
}
